<template>
  <v-row class="mt-3" style="position: relative">
    <v-col v-blur="blurConfig" cols="5">
      <v-card class="pa-5" style="background-color: rgba(0,0,0,0)" width="100%" outlined>
        <v-container fluid>
          <h1>{{ $t('futures.title') }}</h1>
          <v-row>
            <v-col>
              <h2 class="mt-3">{{ getNumber(avgProfitFutures) }}</h2>
              <p>{{ $t('futures.items.avgProfit') }}</p>
              <h2 class="mt-3">{{ getNumber(avgContracts) }}</h2>
              <p>{{ $t('futures.items.avgContracts') }}</p>
            </v-col>
            <v-col>
              <h2 class="mt-3">{{ getNumber(maxMAE) }}</h2>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <p v-bind="attrs" v-on="on">
                    {{ (maxMAEDate !== null) ? `${$tc('futures.items.maxMAE', 2)} ${maxMAEDate.toISOString().substring(0,
                      10)} ${maxMAEDate.toISOString().substring(11, 16)})` : `${$tc('futures.items.maxMAE', 1)}` }}</p>
                </template>
                <span>Max Adverse Excursion</span>
              </v-tooltip>
              <h2 class="mt-3">{{ getNumber(maxMFE) }}</h2>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <p v-bind="attrs" v-on="on">
                    {{ (maxMFEDate !== null) ? `${$tc('futures.items.maxMFE', 2)} ${maxMFEDate.toISOString().substring(0,
                      10)} ${maxMFEDate.toISOString().substring(11, 16)})` : `${$tc('futures.items.maxMFE', 1)}` }}</p>
                </template>
                <span>Max Favorable Excursion</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-col>
    <v-col v-blur="blurConfig" cols="2">
      <h2 class="text-center mb-3">{{ $t('allocation.title') }}</h2>
      <apexchart type="radialBar" height="350" :options="marketAllocationOptions" :series="marketAllocationSeries">
      </apexchart>
    </v-col>
    <v-col v-blur="blurConfig" cols="5">
      <v-card class="pa-5" style="background-color: rgba(0,0,0,0)" width="100%" outlined>
        <v-container fluid>
          <h1>{{ $t('forex.title') }}</h1>
          <v-row>
            <v-col>
              <h2 class="mt-3">{{ getNumber(avgProfitForex) }}</h2>
              <p>{{ $t('forex.items.avgProfit') }}</p>
              <h2 class="mt-3">{{ getNumber(avgLots) }}</h2>
              <p>{{ $t('forex.items.avgLots') }}</p>
            </v-col>
            <v-col>
              <h2 class="mt-3">{{ getNumber(avgSwap) }}</h2>
              <p>{{ $t('forex.items.avgSwap') }}</p>
              <h2 class="mt-3">{{ getNumber(maxSwap) }}</h2>
              <p>{{ (maxSwapDate !== null) ? `${$tc('forex.items.maxSwap', 2)} ${maxSwapDate.toISOString().substring(0,
                10).replaceAll('-', '/')} ${maxSwapDate.toISOString().substring(11, 16)})` : `${$tc('forex.items.maxSwap',
                  1)}` }}</p>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-col>
    <v-overlay v-model="canSeeStat" absolute opacity="0">
      <ProStatAlert @goToPro="goToPro" />
    </v-overlay>
  </v-row>
</template>

<script>
import ProStatAlert from "@/views/Journal/components/ProStatAlert";
export default {
  name: "ByMarketStats",
  components: { ProStatAlert },
  data() {
    return {
      blurConfig: {
        isBlurred: !this.$store.state.user.isPro,
        opacity: 0.07,
        filter: "blur(1.2px)",
        transition: "all .3s linear"
      },
      canSeeStat: !this.$store.state.user.isPro,
      // Forex
      avgProfitForex: 0,
      avgLots: 0,
      avgSwap: 0,
      maxSwap: 0,
      maxSwapDate: null,

      // Futures
      avgProfitFutures: 0,
      avgContracts: 0,
      maxMAE: 0,
      maxMAEDate: null,
      maxMFE: 0,
      maxMFEDate: null,

      marketAllocationSeries: [75, 10],
      marketAllocationOptions: {
        theme: {
          mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
        },
        chart: {
          height: 350,
          type: 'radialBar',
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
              image: undefined,
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                show: false,
              }
            }
          }
        },
        colors: ['#0084ff', '#39539E', '#0077B5'],
        labels: ['Forex', 'Futures'],
        legend: {
          show: true,
          floating: true,
          fontSize: '15px',
          offsetY: -20,
          position: 'left',
          labels: {
            useSeriesColors: true,
          },
          markers: {
            size: 0
          },
          formatter: function (seriesName, opts) {
            return `${opts.w.globals.series[opts.seriesIndex]} ${seriesName} trades`;
          },
          itemMargin: {
            vertical: 3
          }
        },
      },
    };
  },
  watch: {
    '$store.state.book.trades': function () {
      this.refreshStats();
    },
  },
  mounted() {
    this.refreshStats();
  },
  methods: {
    refreshStats() {
      this.marketAllocationSeries = [];

      // Forex
      let forexCount = 0;
      this.avgProfitForex = 0;
      this.avgLots = 0;
      this.avgSwap = 0;
      this.maxSwap = Number.MIN_VALUE;
      this.maxSwapDate = null;

      // Futures
      this.avgProfitFutures = 0;
      this.avgContracts = 0;
      this.maxMAE = Number.MIN_VALUE;
      this.maxMAEDate = null;
      this.maxMFE = Number.MIN_VALUE;
      this.maxMFEDate = null;
      let futuresCount = 0;

      this.$store.state.book.trades.forEach((trade) => {
        // Forex
        if (trade.swap !== null) {
          if (trade.swap > this.maxSwap) {
            this.maxSwap = trade.swap;
            this.maxSwapDate = trade.openTime;
          }
          this.avgProfitForex += trade.netProfit;
          this.avgLots += trade.lots;
          this.avgSwap += trade.swap;
          forexCount++;
        }

        // Futures
        else if (trade.mae !== null) {
          if (trade.mfe > this.maxMFE) {
            this.maxMFE = trade.mfe;
            this.maxMFEDate = trade.openTime;
          }
          if (trade.mae > this.maxMAE) {
            this.maxMAE = trade.mae;
            this.maxMAEDate = trade.openTime;
          }
          this.avgProfitFutures += trade.netProfit;
          this.avgContracts += trade.contracts;
          futuresCount++;
        }
      });

      if (forexCount > 0) {
        this.avgProfitForex /= forexCount;
        this.avgLots /= forexCount;
        this.avgSwap /= forexCount;
      }
      if (futuresCount > 0) {
        this.avgProfitFutures /= futuresCount;
        this.avgContracts /= futuresCount;
      }
      this.marketAllocationSeries = [forexCount, futuresCount];
    },
    getNumber(number) {
      return (number > 1000 || number < -1000) ? `${(number / 1000).toFixed(2)}k` : (number.toFixed(2) * 1).toString();
    },
    goToPro() {
      this.$emit('goToPro');
    }
  },
}
</script>

<i18n>
{
  "en": {
    "futures": {
      "title": "Futures Metrics",
      "items": {
        "avgProfit": "Average Profit on Futures trades",
        "avgContracts": "Average Contracts",
        "maxMAE": "Maximum MAE | Maximum MAE (At",
        "maxMFE": "Maximum MFE | Maximum MFE (At"
      }
    },
    "forex": {
      "title": "Forex Metrics",
      "items": {
        "avgProfit": "Average Profit on Forex trades",
        "avgLots": "Average Lots",
        "avgSwap": "Average Swap",
        "maxSwap": "Maximum Swap | Maximum Swap (At"
      }
    },
    "allocation": {
      "title": "Market Allocation"
    }
  },
  "es": {
    "futures": {
      "title": "Métricas de Futuros",
      "items": {
        "avgProfit": "Ganancias Promedio en trades de Futuros",
        "avgContracts": "Contratos Promedio",
        "maxMAE": "Máximo MAE | Máximo MAE (En",
        "maxMFE": "Máximo MAE | Máximo MFE (En"
      }
    },
    "forex": {
      "title": "Métricas de Forex",
      "items": {
        "avgProfit": "Ganancias Promedio en trades de Forex",
        "avgLots": "Lotes Promedio",
        "avgSwap": "Swap Promedio",
        "maxSwap": "Máximo Swap | Máximo Swap (En"
      }
    },
    "allocation": {
      "title": "Asignación por Mercado"
    }
  }
}
</i18n>

<style scoped></style>
